"use client";

import { Card } from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency } from "@/lib/utils";
import type { SaleHistory } from "@/schemas/analyzed-property";
import { format } from "date-fns";
import {
  ArrowRight,
  BanknoteIcon,
  Building2,
  DollarSign,
  FileText,
  HandCoins,
  Info,
  Receipt,
  Repeat,
  User2,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

interface PriceHistoryEntry {
  date: string;
  price: number;
  event: string;
  pricePerSquareFoot: number;
  source: string;
}

interface TaxHistoryEntry {
  time: number | null;
  value: number | null;
  taxPaid: number | null;
  valueIncreaseRate: number | null;
}

interface PropertyHistoryProps {
  priceHistory?: PriceHistoryEntry[];
  taxHistory?: TaxHistoryEntry[];
  saleHistory?: SaleHistory[];
  squareFeet?: number | null;
}

interface ChartDataPoint {
  date: string;
  price: number;
  event?: string;
  saleInfo?: {
    buyer: string;
    seller: string;
    method?: string;
    type: string;
  } | null;
}

function TransactionIcon({
  type,
  className,
}: { type: string; className?: string }) {
  if (type.includes("Foreclosure")) return <Building2 className={className} />;
  if (type.includes("Cash Purchase"))
    return <HandCoins className={className} />;
  if (type.includes("Financed")) return <BanknoteIcon className={className} />;
  if (type.includes("Intrafamily")) return <User2 className={className} />;
  return <Repeat className={className} />;
}

function TransactionTimeline({ entries }: { entries: SaleHistory[] }) {
  const sortedEntries = [...entries].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <div className="relative space-y-8 before:absolute before:inset-0 before:ml-5 before:h-full before:w-0.5 before:-translate-x-px before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
      {sortedEntries.map((entry) => (
        <div
          key={`${entry.date}-${entry.buyer}-${entry.seller}`}
          className="relative flex items-center justify-between gap-x-8"
        >
          {/* Icon */}
          <div className="absolute left-0 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow ring-1 ring-gray-200">
            <TransactionIcon
              type={entry.transaction_type}
              className="h-5 w-5 text-gray-600"
            />
          </div>

          {/* Content */}
          <div className="ml-16 w-full">
            <Card className="p-4">
              <div className="flex flex-col gap-2">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {formatCurrency(entry.amount)}
                    </span>
                    {entry.purchase_method && (
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                        {entry.purchase_method}
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(entry.date), "MMM d, yyyy")}
                  </span>
                </div>

                {/* Transaction Details */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{entry.seller}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">{entry.buyer}</span>
                </div>

                {/* Footer */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" />
                    {entry.document_type}
                  </div>
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-3.5 w-3.5" />
                    {entry.transaction_type.replace(
                      "Arms Length Residential Transactions",
                      "Standard Sale",
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Checks if two dates are within a specified number of days of each other
 */
function areDatesClose(
  date1: string,
  date2: string,
  daysThreshold = 7,
): boolean {
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= daysThreshold;
  } catch (error) {
    return false;
  }
}

/**
 * Merges price history and sale history data, handling duplicates
 */
function mergeHistoryData(
  priceHistory: PriceHistoryEntry[] = [],
  saleHistory: SaleHistory[] = [],
): ChartDataPoint[] {
  // Convert price history entries to chart data points
  const priceDataPoints: ChartDataPoint[] = priceHistory.map((entry) => ({
    date: entry.date,
    price: entry.price,
    event: entry.event,
    saleInfo: null,
  }));

  // Convert sale history entries to chart data points
  const saleDataPoints: ChartDataPoint[] = saleHistory.map((entry) => ({
    date: entry.date,
    price: entry.amount,
    event: "Sold",
    saleInfo: {
      buyer: entry.buyer,
      seller: entry.seller,
      method: entry.purchase_method,
      type: entry.transaction_type,
    },
  }));

  // Combine all data points
  const allDataPoints = [...priceDataPoints, ...saleDataPoints];

  // Sort by date (oldest first)
  allDataPoints.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  // Remove duplicates (entries with same date and price or very close dates with same price)
  const deduplicatedPoints: ChartDataPoint[] = [];

  for (const point of allDataPoints) {
    // Check if this point is a duplicate of any existing point
    const isDuplicate = deduplicatedPoints.some(
      (existingPoint) =>
        // Same price and either same date or dates are close
        existingPoint.price === point.price &&
        (existingPoint.date === point.date ||
          areDatesClose(existingPoint.date, point.date)),
    );

    if (!isDuplicate) {
      // If sale info exists in both, prefer the one with more complete info
      const existingWithCloseDate = deduplicatedPoints.find((existingPoint) =>
        areDatesClose(existingPoint.date, point.date),
      );

      if (
        existingWithCloseDate &&
        point.saleInfo &&
        existingWithCloseDate.saleInfo
      ) {
        // Both have sale info - keep the more detailed one
        if (
          (point.saleInfo.buyer && !existingWithCloseDate.saleInfo.buyer) ||
          (point.saleInfo.method && !existingWithCloseDate.saleInfo.method)
        ) {
          // Replace the existing point with this one
          const index = deduplicatedPoints.indexOf(existingWithCloseDate);
          deduplicatedPoints[index] = point;
        }
      } else {
        deduplicatedPoints.push(point);
      }
    }
  }

  return deduplicatedPoints;
}

function HistoryTimeline({
  entries,
  saleHistory,
}: {
  entries: PriceHistoryEntry[];
  saleHistory?: SaleHistory[];
}) {
  // Merge and deduplicate data from both sources
  const chartData = mergeHistoryData(entries, saleHistory || []);

  const chartConfig = {
    price: {
      label: "Price",
      color: "#CF9D7C", // plaster color
    },
  };

  return (
    <div className="h-[160px] sm:h-[350px] md:h-[400px] w-full mt-6 mb-16">
      <ChartContainer config={chartConfig}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
          >
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#CF9D7C" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#CF9D7C" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(date: string) => {
                try {
                  const parsedDate = new Date(date);
                  if (Number.isNaN(parsedDate.getTime())) return "Invalid Date";
                  return format(parsedDate, "MMM yyyy");
                } catch (error) {
                  console.error("Error formatting date:", error);
                  return "Invalid Date";
                }
              }}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={(value: number) => formatCurrency(value)}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={80}
            />
            <ChartTooltip
              content={(props) => {
                const { active, payload } = props;
                if (!active || !payload?.length) return null;

                const data = payload[0].payload;
                return (
                  <div className="rounded-lg border bg-background p-4 shadow-sm space-y-2">
                    <div className="space-y-1">
                      <span className="text-[0.70rem] uppercase text-muted-foreground">
                        {format(new Date(data.date), "MMM d, yyyy")}
                      </span>
                      <div className="font-bold text-muted-foreground">
                        {formatCurrency(data.price)}
                      </div>
                    </div>

                    {data.saleInfo && (
                      <div className="space-y-1 border-t pt-2 text-xs">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <User2 className="h-3.5 w-3.5" />
                          <span>{data.saleInfo.seller}</span>
                          <ArrowRight className="h-3 w-3" />
                          <span>{data.saleInfo.buyer}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {data.saleInfo.method && (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">
                              {data.saleInfo.method}
                            </span>
                          )}
                          <span className="text-muted-foreground">
                            {data.saleInfo.type.replace(
                              "Arms Length Residential Transactions",
                              "Standard Sale",
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                    {data.event && (
                      <div className="text-xs text-muted-foreground">
                        {data.event}
                      </div>
                    )}
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke="#CF9D7C"
              strokeWidth={2}
              fill="url(#colorPrice)"
              dot={{
                r: 4,
                fill: "white",
                stroke: "#CF9D7C",
                strokeWidth: 2,
              }}
              activeDot={{
                r: 6,
                fill: "white",
                stroke: "#CF9D7C",
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}

export function PropertyHistory({
  priceHistory,
  taxHistory,
  saleHistory,
  squareFeet,
}: PropertyHistoryProps) {
  if (!priceHistory?.length && !taxHistory?.length && !saleHistory?.length)
    return null;

  // Include ALL price history entries, not just "Sold" or "Listed for sale"
  const allPriceHistory = priceHistory || [];

  const latestTax = taxHistory?.[0];
  const previousTax = taxHistory?.[1];

  // Calculate tax change percentage with proper handling for edge cases
  let taxChangePercent = 0;
  let taxChangeText = "";

  if (latestTax && previousTax) {
    // Only calculate if both values exist and previousTax.taxPaid is not zero or null
    if (previousTax.taxPaid && previousTax.taxPaid !== 0 && latestTax.taxPaid) {
      taxChangePercent =
        (latestTax.taxPaid - previousTax.taxPaid) / previousTax.taxPaid;
      // Format the percentage with proper handling for NaN
      taxChangeText = !Number.isNaN(taxChangePercent)
        ? `${Math.abs(taxChangePercent * 100).toFixed(1)}% from previous year`
        : "";
    } else {
      // Handle case where previous tax was zero or null
      taxChangeText = latestTax.taxPaid ? "New assessment" : "";
    }
  }

  return (
    <div className="space-y-12" id="property-history">
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Property History</h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Info className="h-4 w-4" />
                  <span className="text-sm">About History</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  View the complete history of this property, including price
                  changes, ownership transfers, and transaction details.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Price History Chart */}
        {(allPriceHistory.length > 0 || saleHistory?.length) && (
          <div className="mt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-3">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm font-medium">Price History</span>
            </div>
            <HistoryTimeline
              entries={allPriceHistory}
              saleHistory={saleHistory}
            />
          </div>
        )}

        {/* Ownership Timeline */}
        {saleHistory && (
          <div className="mt-8">
            <div className="flex items-center gap-2 text-muted-foreground mb-6">
              <User2 className="w-4 h-4" />
              <span className="text-sm font-medium">Ownership Timeline</span>
            </div>
            <TransactionTimeline entries={saleHistory} />
          </div>
        )}
      </div>

      {/* Tax History */}
      {latestTax && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Property Taxes</h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent>
                  Annual property tax assessment and history
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <Card className="p-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-blue-50">
                <Receipt className="h-5 w-5 text-blue-500" />
              </div>

              <div className="flex-1">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-2xl font-semibold">
                      {formatCurrency(latestTax.taxPaid ?? 0)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {latestTax.time
                        ? format(new Date(latestTax.time), "yyyy")
                        : "N/A"}{" "}
                      Tax Assessment
                    </div>
                  </div>
                  {taxChangeText && (
                    <div className="flex items-center gap-1 text-sm">
                      <span
                        className={
                          taxChangePercent > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }
                      >
                        {Math.abs(taxChangePercent * 100).toFixed(1)}%
                      </span>
                      <span className="text-gray-500">from previous year</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 text-sm text-gray-600">
                  <div className="flex items-center justify-between">
                    <span>Assessed Value</span>
                    <span className="font-medium">
                      {formatCurrency(latestTax.value ?? 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span>Tax Rate</span>
                    <span className="font-medium">
                      {latestTax.value && latestTax.taxPaid
                        ? ((latestTax.taxPaid / latestTax.value) * 100).toFixed(
                            2,
                          )
                        : "0.00"}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
